import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import MoreSvg from "../assets/svg/more.svg";
import useUserStore from "../store/userStore";
import useLanguageStore from "../store/languageStore";
import { useTranslation } from "../i18n/useTranslation";
import { cn } from "../lib/utils";
import { Button, Select } from "antd";
import { Globe } from "lucide-react";

const Header = () => {
  const [isOnTop, setIsOnTop] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const menuWrapRef = useRef(null);
  const userDropdownRef = useRef(null);
  const location = useLocation();
  const { isLoggedIn, user, logout } = useUserStore();
  const { language, setLanguage } = useLanguageStore();
  const { t } = useTranslation();

  const languageOptions = [
    { value: "zh", label: t("languageZh") },
    { value: "en", label: t("languageEn") },
  ];

  const logoUrl = require("../assets/img/Stabilize-Ai2.png");

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    setIsOnTop(scrollTop === 0);
  };

  const handleClickOutside = (e) => {
    if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) {
      setShowMenu(false);
    }
    if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
      setShowUserDropdown(false);
    }
  };

  // 路由切换后关闭菜单
  useEffect(() => {
    setShowMenu(false);
    setShowUserDropdown(false);
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleNavClick = () => {
    setShowMenu(false);
  };

  const isAccountPage = location.pathname.startsWith("/account");
  const useLightStyle = isOnTop && !isAccountPage;

  return (
    <>
      {useLightStyle && (
        <style>{`
          .language-select-white .ant-select-selector {
            background-color: transparent !important;
            border-color: rgba(255, 255, 255, 0.3) !important;
          }
          .language-select-white .ant-select-selection-item {
            color: white !important;
          }
          .language-select-white .ant-select-arrow {
            color: white !important;
          }
        `}</style>
      )}
      <header
        ref={menuWrapRef}
        className={`header-wrap fixed inset-x-0 top-0 z-30 duration-300 ease-in-out ${
          isAccountPage
            ? "bg-white text-[#004b67] shadow-sm"
            : useLightStyle
              ? "bg-[#ffffff00] text-white"
              : "bg-[#ffffffff] text-[#004b67] shadow-lg"
        }`}
      >
      <div className="container flex items-center justify-between py-2 md:py-0 relative">
        <div className="flex items-center gap-2">
          <Link to="/" onClick={handleNavClick}>
            <img className="h-8" src={logoUrl} alt="Logo" />
          </Link>
          {/* 语言切换在 Logo 右侧，留一定间距 */}
          <div className={cn("flex items-center gap-1 ml-4", useLightStyle && "language-select-white")}>
            <Globe size={16} className={useLightStyle ? "text-white" : "text-[#004b67]"} />
            <Select
              value={language}
              onChange={setLanguage}
              options={languageOptions}
              className="min-w-[72px]"
              size="small"
              bordered={false}
              popupMatchSelectWidth={false}
              dropdownStyle={{ minWidth: "120px" }}
            />
          </div>
        </div>

        <div className="hidden md:flex items-center">
          <Link
            className={cn(
              "px-4 py-6 text-sm",
              location.pathname === "/" && "font-bold"
            )}
            to="/"
            onClick={handleNavClick}
          >
            {t("home")}
          </Link>
          <Link
            className={cn(
              "px-4 py-6 text-sm",
              location.pathname === "/technology" && "font-bold"
            )}
            to="/technology"
            onClick={handleNavClick}
          >
            {t("technology")}
          </Link>
          <Link
            className={cn(
              "px-4 py-6 text-sm",
              location.pathname === "/solutions" && "font-bold"
            )}
            to="/solutions"
            onClick={handleNavClick}
          >
            {t("solutions")}
          </Link>

          <Link
            className={cn(
              "px-4 py-6 text-sm",
              location.pathname === "/api-doc" && "font-bold"
            )}
            to="/api-doc"
            onClick={handleNavClick}
          >
            {t("apiDoc")}
          </Link>

          <Link
            className={cn(
              "px-4 py-6 text-sm",
              location.pathname === "/about" && "font-bold"
            )}
            to="/about"
            onClick={handleNavClick}
          >
            {t("about")}
          </Link>

          {isLoggedIn ? (
            <div className="flex items-center gap-2 ml-4">
              <div
                ref={userDropdownRef}
                className="relative inline-block"
                onMouseEnter={() => setShowUserDropdown(true)}
                onMouseLeave={() => setShowUserDropdown(false)}
              >
                <span className="px-4 py-6 text-sm cursor-pointer block">
                  {t("welcome")}{user?.name || user?.email}
                </span>
                {showUserDropdown && (
                  <div className="absolute left-1/2 top-full -translate-x-1/2 min-w-[160px] rounded-md bg-white text-[#004b67] shadow-lg border border-gray-100 py-1 z-50">
                  <Link
                    className={cn(
                      "block px-4 py-2 text-sm hover:bg-gray-50",
                      location.pathname === "/account" || location.pathname === "/account/overview" ? "font-bold" : ""
                    )}
                    to="/account/overview"
                    onClick={handleNavClick}
                  >
                    {t("accountOverview")}
                  </Link>
                  <Link
                    className={cn(
                      "block px-4 py-2 text-sm hover:bg-gray-50",
                      location.pathname === "/account/subscription" ? "font-bold" : ""
                    )}
                    to="/account/subscription"
                    onClick={handleNavClick}
                  >
                    {t("accountSubscription")}
                  </Link>
                  <Link
                    className={cn(
                      "block px-4 py-2 text-sm hover:bg-gray-50",
                      location.pathname === "/account/devices" ? "font-bold" : ""
                    )}
                    to="/account/devices"
                    onClick={handleNavClick}
                  >
                    {t("accountDevices")}
                  </Link>
                  <Link
                    className={cn(
                      "block px-4 py-2 text-sm hover:bg-gray-50",
                      location.pathname === "/account/security" ? "font-bold" : ""
                    )}
                    to="/account/security"
                    onClick={handleNavClick}
                  >
                    {t("accountSecurity")}
                  </Link>
                </div>
                )}
              </div>
              <Button
                onClick={() => {
                  logout();
                  handleNavClick();
                }}
                type="primary"
              >
                {t("logout")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 ml-4">
              <Link
                className="px-4 h-[32px] leading-[32px] text-sm border border-white rounded hover:bg-white hover:text-[#004b67]"
                to="/login"
                onClick={handleNavClick}
              >
                {t("login")}
              </Link>
            </div>
          )}
        </div>

        <div className="relative md:hidden flex items-center gap-2">
          <img
            src={MoreSvg}
            className={cn(
              "icon-more w-[26px] h-[26px] cursor-pointer",
              useLightStyle ? "invert" : ""
            )}
            onClick={() => setShowMenu(!showMenu)}
            alt="Menu"
          />
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 min-w-[160px] max-w-[90vw] rounded-md bg-white text-left text-[#004b67] shadow-lg border border-gray-100 py-1 z-50 md:hidden max-h-[70vh] overflow-y-auto">
              <div className="px-3 py-2 text-xs font-medium text-gray-400 border-b border-gray-100">
                {t("navSite")}
              </div>
              <Link
                className={cn(
                  "block px-4 py-2.5 text-sm whitespace-nowrap",
                  location.pathname === "/" && "font-bold"
                )}
                to="/"
                onClick={handleNavClick}
              >
                {t("home")}
              </Link>
              <Link
                className={cn(
                  "block px-4 py-2.5 text-sm whitespace-nowrap",
                  location.pathname === "/technology" && "font-bold"
                )}
                to="/technology"
                onClick={handleNavClick}
              >
                {t("technology")}
              </Link>
              <Link
                className={cn(
                  "block px-4 py-2.5 text-sm whitespace-nowrap",
                  location.pathname === "/solutions" && "font-bold"
                )}
                to="/solutions"
                onClick={handleNavClick}
              >
                {t("solutions")}
              </Link>
              <Link
                className={cn(
                  "block px-4 py-2.5 text-sm whitespace-nowrap",
                  location.pathname === "/api-doc" && "font-bold"
                )}
                to="/api-doc"
                onClick={handleNavClick}
              >
                {t("apiDoc")}
              </Link>
              <Link
                className={cn(
                  "block px-4 py-2.5 text-sm whitespace-nowrap",
                  location.pathname === "/about" && "font-bold"
                )}
                to="/about"
                onClick={handleNavClick}
              >
                {t("about")}
              </Link>

              {isLoggedIn && (
                <>
                  <div className="px-3 py-2 mt-1 text-xs font-medium text-gray-400 border-t border-b border-gray-100">
                    {t("navAccount")}
                  </div>
                  <div className="block px-4 py-2 text-sm text-gray-500 truncate">
                    {t("welcome")}{user?.name || user?.email}
                  </div>
                  <Link
                    className={cn("block px-4 py-2.5 text-sm whitespace-nowrap", (location.pathname === "/account" || location.pathname === "/account/overview") && "font-bold")}
                    to="/account/overview"
                    onClick={handleNavClick}
                  >
                    {t("accountOverview")}
                  </Link>
                  <Link
                    className={cn("block px-4 py-2.5 text-sm whitespace-nowrap", location.pathname === "/account/subscription" && "font-bold")}
                    to="/account/subscription"
                    onClick={handleNavClick}
                  >
                    {t("accountSubscription")}
                  </Link>
                  <Link
                    className={cn("block px-4 py-2.5 text-sm whitespace-nowrap", location.pathname === "/account/devices" && "font-bold")}
                    to="/account/devices"
                    onClick={handleNavClick}
                  >
                    {t("accountDevices")}
                  </Link>
                  <Link
                    className={cn("block px-4 py-2.5 text-sm whitespace-nowrap", location.pathname === "/account/security" && "font-bold")}
                    to="/account/security"
                    onClick={handleNavClick}
                  >
                    {t("accountSecurity")}
                  </Link>
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50"
                    onClick={() => {
                      logout();
                      handleNavClick();
                    }}
                  >
                    {t("logout")}
                  </button>
                </>
              )}
              {!isLoggedIn && (
                <Link
                  className="block px-4 py-2.5 text-sm whitespace-nowrap"
                  to="/login"
                  onClick={handleNavClick}
                >
                  {t("login")}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
